import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ConfirmDialog } from '../../../components/ui';

export interface ConfirmRequest {
  /** Names the object: "Clear 12 desk assignments on Level 2?" */
  title: string;
  /** States the consequence in one or two plain sentences. */
  body?: string;
  /** Repeats the verb: "Clear 12 assignments". */
  confirmLabel: string;
  destructive?: boolean;
}

/**
 * An in-app replacement for `window.confirm` that keeps the same awaitable
 * shape: `if (!(await confirm({...}))) return;`. Dismissing resolves false.
 */
export const useConfirmDialog = () => {
  const [open, setOpen] = useState(false);
  // Kept after close so the dialog's exit transition still shows its words.
  const [content, setContent] = useState<ConfirmRequest | null>(null);
  const resolverRef = useRef<((confirmed: boolean) => void) | null>(null);

  const settle = useCallback((confirmed: boolean) => {
    resolverRef.current?.(confirmed);
    resolverRef.current = null;
    setOpen(false);
  }, []);

  const confirm = useCallback((request: ConfirmRequest) => new Promise<boolean>((resolve) => {
    resolverRef.current?.(false);
    resolverRef.current = resolve;
    setContent(request);
    setOpen(true);
  }), []);

  useEffect(() => () => {
    resolverRef.current?.(false);
    resolverRef.current = null;
  }, []);

  const dialog = (
    <ConfirmDialog
      open={open}
      onClose={() => settle(false)}
      onConfirm={() => settle(true)}
      title={content?.title ?? ''}
      body={content?.body}
      confirmLabel={content?.confirmLabel ?? 'Continue'}
      destructive={content?.destructive}
    />
  );

  return { confirm, dialog };
};
